import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Notification, Spin } from 'Components';
import { FormikErrors, FormikHelpers } from 'formik';
import { useEffect, useState } from 'react';
import { isEmpty, isEqual } from 'lodash';
import { useHistory, useParams } from 'react-router';
import { UPDATE_USER, RESET_MFA } from 'Services/Api/Users/Mutations';
import { GET_USER } from 'Services/Api/Users/Queries';
import {
	GetUserResponse,
	PermissionCode,
	UpdateUserRequest,
} from 'Services/Api/Users/Types';
import EditUserForm from './EditUserForm';
import { getUserRoleLabel } from 'app-util';
import { APIErrorCodes } from 'app-types';
import { checkApolloError } from 'Helpers/graphql';
import { navigateBack } from 'Helpers/navigation';
import { EditUserValues } from '../User.types';
import { getInitialValues, getEditUserSubmitValues } from './EditUser.helpers';
import { useErrorBoundary } from 'Hooks';
import { getAppRoutes } from 'Pages/App/App.constants';
import { useIsModuleSettingDisabled } from './tabs/ModuleSettings/ModuleSettingsContainer';
import {
	isModuleSettingsValidationError,
	validateLMSModuleSettings
} from './tabs/ModuleSettings/ModuleSettings.helpers';
import { useUser } from '../User.hooks';
import { schema } from '../schema';
import { updateUserCustomRoles } from '../service/index.Service';
import { useDashboardContext } from 'Pages/Dashboard/DashboardContext';
import { useStores } from 'Hooks/useStore';
import { SystemRoleType } from 'Services/Api/Roles/Types';

function EditUser() {
	const { t } = useTranslation();
	const history = useHistory();
	const params = useParams<{ peakV3Id: string }>();
	const errorBoundary = useErrorBoundary();
	const { authStore } = useStores();
	const { injectMeta } = useUser();
	const [isLmsHasError, setLmsHasError] = useState<boolean>(false);
	const { getIsLMSModuleSettingsDisabled } = useIsModuleSettingDisabled();
	const [updateUser] = useMutation<unknown, UpdateUserRequest>(UPDATE_USER);
	const [getUser, getUserResponse] = useLazyQuery<GetUserResponse>(GET_USER, {
		variables: {
			peakV3Id: params.peakV3Id
		},
		fetchPolicy: 'no-cache',
		onError: (error: ApolloError) => {
			const apolloError = checkApolloError(error);
			if (apolloError.is(APIErrorCodes.PeakV3IDMustBeaUUID)) {
				errorBoundary.setStatus('notFound');
			} else if (apolloError.is(APIErrorCodes.GroupsNotInHierarchy)) {
				errorBoundary.setStatus('forbidden');
			} else {
				errorBoundary.setStatus('internal');
			}
		}
	});
	const { setToastInfo } = useDashboardContext();
	const user = getUserResponse.data?.findUser;
	const initialValues = user ? getInitialValues(user) : null;

	const goBack = () => {
		navigateBack(history, getAppRoutes().USERS);
	};

	const onSubmit = async (
		editUserValues: EditUserValues,
		helpers: FormikHelpers<EditUserValues>
	) => {
		const { customTenantRoleIds: _customTenantRoleIds, ...values } = editUserValues;
		let customRolesUpdateFailed = false;
		const canAssignSecondaryRole = authStore.currentUser?.primarySystemRole?.permissions?.some(r =>
			r.code === PermissionCode.ASSIGN_SECONDARY_ROLE
		);
		const isSuperAdmin = authStore.currentUser?.primarySystemRole?.role === SystemRoleType.SuperAdmin;
		try {
			const submitValues = getEditUserSubmitValues(editUserValues);
			const { customTenantRoleIds: roleIds, ...editValues } = submitValues;
			const peakV3Id = editUserValues.peakV3Id;

			if (!isEqual(initialValues, values)) {
				const isModuleSettingsDisabled = getIsLMSModuleSettingsDisabled(
					values
				);
				setLmsHasError(false);
				validateLMSModuleSettings(isModuleSettingsDisabled, values);

				await updateUser({
					variables: {
						input: editValues
					}
				});
			}

			if (peakV3Id && (canAssignSecondaryRole || isSuperAdmin)) {
				try {
					await updateUserCustomRoles(peakV3Id, roleIds ?? []);
				} catch (e) {
					console.error('Failed to update custom roles', e);
					customRolesUpdateFailed = true;
				}
			}
			if(!!customRolesUpdateFailed) {
				setToastInfo?.({ message: t(`errorCodes.customRolesUpdateFailed`), type: 'warning' });
			} else {
				setToastInfo?.({ message: t('notification.edit.userChanges'), type: 'success', icon: "check" });
			}
			helpers.resetForm({ values });
			goBack();
		} catch (e) {
			const error = e as Error;
			helpers.setSubmitting(false);
			if (isModuleSettingsValidationError(error)) {
				setLmsHasError(true);
				helpers.setErrors({
					moduleSettings: error.message
				});
				return;
			}
			const apolloError = checkApolloError(error);
			const errors =
				apolloError.getFieldErrors(
					t,
					injectMeta(schema.describe().fields),
					'user.errorCodes'
				) ?? {};

			if (!isEmpty(errors)) {
				helpers.setErrors(
					(errors as unknown) as FormikErrors<EditUserValues>
				);
			} else if (
				apolloError.is(APIErrorCodes.MovingBetweenTenantsForbidden)
			) {
				setToastInfo?.({
					message: t(
						`errorCodes.${APIErrorCodes.MovingBetweenTenantsForbidden}`
					),
					type: 'warning'
				});
			} else if (apolloError.is(APIErrorCodes.Forbidden)) {
				setToastInfo?.({
					message: t(`errorCodes.${APIErrorCodes.Forbidden}`),
					type: 'warning'
				});
			} else if (
				apolloError.is(APIErrorCodes.ValidationIsCognitoPhoneNumber)
			) {
				helpers.setErrors({
					phoneNumber: t(
						`user.errorCodes.${APIErrorCodes.ValidationIsCognitoPhoneNumber}`
					)
				});
			} else {
				setToastInfo?.({
					message: t(`errorCodes.genericErrorMessage`),
					type: 'warning'
				});
			}
		}
	};

	useEffect(() => {
		getUser();
		// eslint-disable-next-line
	}, []);

	if (!getUserResponse.called || getUserResponse.loading) {
		return <Spin />;
	}

	if (!user) {
		return null;
	}

	const userName = `${user.firstName} ${user.lastName}`;

	return (
		<EditUserForm
			userName={userName}
			userRole={getUserRoleLabel(user.primarySystemRole.role)}
			isProfile={false}
			initialValues={initialValues as EditUserValues}
			onSubmit={onSubmit}
			goBack={goBack}
			isLmsHasError={isLmsHasError}
			twoFactorAuthenticationStatus={user.twoFactorAuthorizationStatus}
		/>
	);
}

export default EditUser;
